/**
 * Hyperframes port of `src/scenes/data/bigNumber.tsx`.
 *
 * The number counts up via a GSAP `onUpdate` callback. Hyperframes seeks
 * the (paused) timeline frame-by-frame during render, so `onUpdate`
 * fires deterministically at every frame.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import { escapeHtml } from "../../composition.js";
import { typeSize, accentFor, makeTl, clipOutroFade, combineSceneFragments, weights, withAlpha, colors as themeColors } from "../../reel/sceneShared.js";
import { tl } from "../../timeline.js";

const COUNT_START_SEC = 4 / 30;
const COUNT_DUR_SEC = 1.4;

export const bigNumber: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const targetNum = parseFloat((inputs.number ?? "73").replace(/[^0-9.\-]/g, "")) || 0;
  const unit = inputs.unit ?? "";
  const label = inputs.text ?? "Inserate live";
  const subtext = inputs.subtext ?? "";
  const isInt = Number.isInteger(targetNum);

  const megaSize = typeSize("mega", ctx.format);
  const lgSize = typeSize("lg", ctx.format);
  const smSize = typeSize("sm", ctx.format);

  const t = makeTl(clipStartSec);
  const numId = `${clipPrefix}-num`;
  const labelId = `${clipPrefix}-label`;
  const subId = `${clipPrefix}-sub`;
  const wrapId = `${clipPrefix}-wrap`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:24px;padding:96px;">
  <div id="${numId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${megaSize * 0.95}px;line-height:.95;letter-spacing:-3.5px;color:${themeColors.fg};text-shadow:0 0 80px ${withAlpha(accent, 0.5)},0 0 24px ${withAlpha(accent, 0.3)};font-variant-numeric:tabular-nums;">0${escapeHtml(unit)}</div>
  <div id="${labelId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${lgSize * 0.7}px;letter-spacing:-0.4px;color:${withAlpha(themeColors.fg, 0.92)};text-align:center;max-width:${ctx.format === "9:16" ? 760 : 1100}px;line-height:1.05;">${escapeHtml(label)}</div>
  ${subtext ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${smSize}px;color:${withAlpha(themeColors.fg, 0.65)};text-align:center;max-width:760px;">${escapeHtml(subtext)}</div>` : ""}
</div>`;

  const init = [
    `gsap.set("#${numId}", { scale: 0.6, opacity: 0 });`,
    `gsap.set("#${labelId}", { opacity: 0, y: 14 });`,
    subtext ? `gsap.set("#${subId}", { opacity: 0 });` : "",
  ]
    .filter(Boolean)
    .join("\n");

  const labelStart = COUNT_START_SEC + COUNT_DUR_SEC;
  const tweens: string[] = [
    t.to(`#${numId}`, { scale: 1, opacity: 1, duration: 0.6, ease: "raw:\"back.out(2)\"" }, 0),
    `tl.to({v:0},{v:${targetNum},duration:${COUNT_DUR_SEC},ease:"power3.out",onUpdate:function(){var v=this.targets()[0].v;document.getElementById(${JSON.stringify(numId)}).textContent=(${isInt ? "Math.round(v)" : "v.toFixed(1)"})+${JSON.stringify(unit)};}},${(clipStartSec + COUNT_START_SEC).toFixed(4)});`,
    t.to(`#${labelId}`, { opacity: 1, y: 0, duration: 0.47, ease: "raw:\"power1.out\"" }, labelStart),
  ];
  if (subtext) tweens.push(t.to(`#${subId}`, { opacity: 1, duration: 0.47, ease: "raw:\"power1.out\"" }, labelStart));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([
    backdrop,
    { html, init, tweens },
  ]);
};

void tl;
